// Lib
import {
  ApolloClient,
  ApolloLink,
  NormalizedCacheObject,
  Observable,
} from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import { InMemoryCache } from 'apollo-cache-inmemory';
import { split } from 'apollo-link';
import { setContext } from 'apollo-link-context';
import { createUploadLink } from 'apollo-upload-client';
import { getMainDefinition } from 'apollo-utilities';
import { Client as WSClient, createClient } from 'graphql-ws';
import { print } from 'graphql';
import fetch from 'node-fetch';
import Store from './store';

// Actions
import { ShowToast } from '@actions/toast';
import { AccountLogoutToLogin } from '@actions/account';

// Services
import {
  ErrorCategory,
  ErrorSeverity,
  handleError,
} from '@services/errorService';
import { getActiveSessionToken } from '@services/sessionToken';
import { invalidateOnMutationLink } from '@utils/invalidateOnMutationLink';

interface Definition {
  kind: string;
  operation?: string;
}

// console.log(`%cDEBUG | apollo.js | Initializing...`, 'color:orange');
const getUrl = (isServer = false): string => {
  const PROD_URL = 'mrbl.bio';
  const PROD_SERVER_URL = 'api.mrbl.bio';

  const DEV_URL = `dev.mrbl.bio`;
  const DEV_SERVER_URL = `api-dev.mrbl.bio`;

  // console.log(
  //   'app env',
  //   process.env.REACT_APP_ENVIRONMENT,
  //   process.env.REACT_APP_BUILD_SHA,
  // );
  switch (process.env.REACT_APP_ENVIRONMENT) {
    case 'dev':
      return isServer ? DEV_SERVER_URL : DEV_URL;
    case 'master':
      return isServer ? PROD_SERVER_URL : PROD_URL;
    case 'feat/custom-domain':
      return isServer ? PROD_SERVER_URL : PROD_URL;
    default:
      return isServer ? DEV_SERVER_URL : DEV_URL;
  }
};

const ORIGIN_SERVER_URL = `https://${getUrl(true)}`;
const ORIGIN_URL = `https://${getUrl()}`;

// Talk to the LOCAL backend whenever the app is actually being served from a
// localhost origin — regardless of NODE_ENV. The webpack dev server runs with
// NODE_ENV=development, but local E2E serves the *production* build statically
// (deterministic, fully-compiled assets — no first-request lazy compile race),
// and that build has NODE_ENV=production baked in. Keying the backend URL off
// the runtime origin (not the build-time NODE_ENV) lets the same prod build
// hit localhost:3001 when served locally and api-dev/api when deployed. This
// mirrors the existing runtime `location.href.includes('localhost')` checks in
// the Checkout components.
const isLocalhostOrigin =
  typeof window === 'object' &&
  /^(localhost|127\.0\.0\.1|\[::1\])$/.test(window.location.hostname);
const API_URL =
  process.env.NODE_ENV === 'production' && !isLocalhostOrigin
    ? `${ORIGIN_SERVER_URL}/api`
    : 'http://localhost:3001/api';

let cache = new InMemoryCache();

const httpLink = (ssrMode: boolean): ReturnType<typeof createUploadLink> => {
  const ssrFetch = (
    ...params: Parameters<typeof fetch>
  ): ReturnType<typeof fetch> => {
    // for debug purposes only.
    const [, options] = params;
    const _body = (options as RequestInit)?.body
      ? JSON.parse((options as RequestInit).body as string)
      : null;
    return fetch(...params)
      .then(async (result) => {
        return result;
      })
      .catch((err) => {
        handleError(err, { category: ErrorCategory.NETWORK, silent: true });
        throw err;
      });
  };

  return createUploadLink({
    uri: `${API_URL}/graphql`,
    fetch: ssrMode ? ssrFetch : fetch, // needs for server render
  });
};

const auth = setContext(
  (_, { headers }: { headers?: Record<string, string> }) => {
    const token = getActiveSessionToken();
    return {
      headers: {
        ...headers,
        authorization: token ? `Bearer ${token}` : '',
      },
    };
  },
);

// Store WebSocket client reference for reconnection
let wsClient: WSClient | null = null;

// Reconnect broadcast. Per ADR-009 (see docs/architecture/subscriptions.md in
// mrbl-ai/server-rust and docs/specs/subscriptions-adr-009-compliance.md here),
// every consumer of an active subscription must refetch the underlying query
// when the WS reconnects (deploy, network blip, idle timeout, etc.). We register
// listeners on a module-level Set and fan out every reconnect.
const reconnectListeners = new Set<() => void>();
let hasConnectedOnce = false;
// Fan out reconnect-refetches only after a REAL drop (a `closed` arrived since
// the last connect) — never on the first connect or a churn-driven reopen.
let sawCloseSinceConnect = false;

const fireReconnectListeners = (): void => {
  reconnectListeners.forEach((listener) => {
    try {
      listener();
    } catch (err) {
      handleError(err as Error, {
        category: ErrorCategory.NETWORK,
        silent: true,
      });
    }
  });
};

const createRestartableWSLink = (url: string): ApolloLink => {
  wsClient = createClient({
    url,
    keepAlive: 10_000, // send ping every 10s to keep connection alive through proxies
    // Don't tear the socket down the instant the active-subscription count hits
    // zero. Switching the creator drawer briefly unsubscribes the old auth's
    // subs before the new ones start; without a grace window the socket closes
    // and reopens, and that reopen fans a refetch storm out to every
    // useWsReconnect consumer (CreatorSummary / DeepLookupResult /
    // BackgroundTasksTray / UnreadNotificationsCount / AIChatThinkingLog).
    lazyCloseTimeout: 20_000,
    retryAttempts: Infinity,
    shouldRetry: () => true,
    connectionParams: () => {
      const token = getActiveSessionToken();
      return token ? { Authorization: `Bearer ${token}` } : {};
    },
    on: {
      closed: () => {
        if (hasConnectedOnce) sawCloseSinceConnect = true;
      },
      connected: () => {
        if (hasConnectedOnce && sawCloseSinceConnect) {
          fireReconnectListeners();
        }
        sawCloseSinceConnect = false;
        hasConnectedOnce = true;
      },
    },
  });

  return new ApolloLink((operation) => {
    return new Observable((sink) => {
      return wsClient!.subscribe(
        { ...operation, query: print(operation.query) },
        {
          next: sink.next.bind(sink),
          complete: sink.complete.bind(sink),
          error: sink.error.bind(sink),
        },
      );
    });
  });
};

// Restart WebSocket connection to pick up the new active session token.
export const restartWebSocketConnection = (): void => {
  if (wsClient) {
    wsClient.terminate();
  }
};

/**
 * Register a callback that fires every time the WS reconnects (not on the
 * first connection). Returns an unregister function for cleanup.
 *
 * Per ADR-009, subscription hooks must refetch their underlying query on
 * every reconnect. Use the `useWsReconnect` React hook for ergonomic
 * lifecycle integration.
 */
export const onWsReconnect = (callback: () => void): (() => void) => {
  reconnectListeners.add(callback);
  return () => {
    reconnectListeners.delete(callback);
  };
};

interface ClientParams {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  link: any;
  cache: InMemoryCache;
}

const getClientParams = (ssrMode: boolean): ClientParams => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let link: any = auth.concat(httpLink(ssrMode) as any);
  if (typeof window === 'object') {
    const SOCKETS_URL =
      process.env.NODE_ENV === 'production' && !isLocalhostOrigin
        ? `wss://${getUrl(true)}/api/graphql/ws`
        : 'ws://localhost:3001/api/graphql/ws';

    const wsLink = createRestartableWSLink(SOCKETS_URL);

    link = split(
      // split based on operation type
      ({ query }) => {
        const { kind, operation } = getMainDefinition(query) as Definition;
        return kind === 'OperationDefinition' && operation === 'subscription';
      },
      wsLink as any,
      link,
    );

    cache = new InMemoryCache().restore(window.__APOLLO_STATE__);
  }
  return {
    link,
    cache,
  };
};

const errorLink = onError(({ graphQLErrors, networkError }) => {
  if (graphQLErrors) {
    graphQLErrors.forEach((error) => {
      handleError(error, {
        category: ErrorCategory.GRAPHQL,
        severity: ErrorSeverity.MEDIUM,
        silent: true,
        extra: {
          locations: error.locations,
          path: error.path,
        },
      });
    });
  }
  if (networkError) {
    handleError(networkError, {
      category: ErrorCategory.NETWORK,
      severity: ErrorSeverity.HIGH,
      silent: true,
    });
  }
});

/**
 * Backend error codes that mean "billing / upstream provider", not "something
 * broke". They are typed server-side (decision 9109) precisely so the UI can
 * tell an empty vendor balance apart from a crash — before this, a HikerAPI
 * `402` arrived as `INTERNAL_ERROR` and read as a broken product.
 *
 * Only `INSUFFICIENT_BALANCE` is actionable by the user; the other two are ours
 * to fix, so their copy says so instead of asking the user to retry forever.
 */
const BILLING_ERROR_COPY: Record<string, { title: string; message: string }> = {
  INSUFFICIENT_BALANCE: {
    title: 'Out of credits',
    message:
      'Add credits to keep fetching fresh data. Everything already indexed stays available.',
  },
  PROVIDER_QUOTA_EXHAUSTED: {
    title: 'Live data paused',
    message:
      'Fresh Instagram data is temporarily unavailable — we are on it. Indexed creators and posts are unaffected.',
  },
  PROVIDER_UNAVAILABLE: {
    title: 'Live data delayed',
    message:
      'The data provider is slow or unreachable right now. Indexed data still works; try the refresh again shortly.',
  },
};

const BILLING_ERROR_CODES = Object.keys(BILLING_ERROR_COPY);

const isUnsupportedInvitationQuery = (
  operationName: string | undefined,
  message: string,
): boolean =>
  operationName === 'PendingOrganizationInvitations' &&
  message.includes(
    'Unknown field "pendingOrganizationInvitations" on type "QueryRoot"',
  );

/* Same mixed-version guard as the invitations query above. `authsByIds` is not
 * on every GraphQL server yet, and the property detail refetches identities on
 * every poll — while a brief generates that is a red toast every few seconds,
 * twice, for a side query whose failure the UI already absorbs (the hook falls
 * back to an empty account list). */
const isUnsupportedAuthsByIdsQuery = (
  operationName: string | undefined,
  message: string,
): boolean =>
  operationName === 'PropertyAccountIdentities' &&
  (message.includes('Unknown field "authsByIds" on type "QueryRoot"') ||
    message.includes('Unknown type "AuthsByIdsInput"'));

const error = onError(({ graphQLErrors, networkError, operation }) => {
  const suppressGlobalErrorToast =
    operation.getContext().suppressGlobalErrorToast === true;

  if (graphQLErrors) {
    graphQLErrors.forEach((error) => {
      const { message } = error;

      // Handle auth errors
      if (
        isUnsupportedInvitationQuery(operation.operationName, message) ||
        isUnsupportedAuthsByIdsQuery(operation.operationName, message)
      ) {
        // Keep mixed-version deploys quiet while the invitations field is not
        // available on every GraphQL server yet. The hook safely renders an
        // empty list and will begin returning invitations once the field lands.
        handleError(error, {
          category: ErrorCategory.GRAPHQL,
          extra: { message, operationName: operation.operationName },
          silent: true,
        });
      } else if (
        [
          'Invalid organization token',
          'Insufficient permissions',
          'Invalid account token',
          'Authentication required',
        ]?.includes(message)
      ) {
        handleError(error, {
          category: ErrorCategory.AUTH,
          severity: ErrorSeverity.HIGH,
          silent: true,
        });
        Store.dispatch(
          ShowToast({
            title: 'Session Expired',
            message: 'Your session has expired. Please log in again.',
          }),
        );
        Store.dispatch(AccountLogoutToLogin());
      } else if (BILLING_ERROR_CODES.includes(String(error.extensions?.code))) {
        // Billing / upstream-provider conditions are NOT product failures, and
        // showing them as a generic red "Error" toast is how an empty vendor
        // balance came to look like a broken app during the 2026-08-04 outage.
        // Each gets its own copy, and none of them is styled as a crash;
        // anything already indexed keeps rendering underneath.
        const billing = BILLING_ERROR_COPY[String(error.extensions?.code)];
        handleError(error, {
          category: ErrorCategory.GRAPHQL,
          extra: { message, code: error.extensions?.code },
          silent: true,
        });
        if (!suppressGlobalErrorToast) {
          Store.dispatch(
            ShowToast({ title: billing.title, message: billing.message }),
          );
        }
      } else {
        // Log other GraphQL errors
        handleError(error, {
          category: ErrorCategory.GRAPHQL,
          extra: { message },
          silent: true,
        });
        const userMessage = message.replace(/^Bad Request:\s*/i, '');
        if (!suppressGlobalErrorToast) {
          Store.dispatch(
            ShowToast({
              title: 'Error',
              message: userMessage,
            }),
          );
        }
      }
    });
  }
  if (networkError) {
    handleError(networkError, {
      category: ErrorCategory.NETWORK,
      severity: ErrorSeverity.HIGH,
      silent: true,
    });

    const statusCode = (networkError as { statusCode?: number }).statusCode;
    let title = 'Network Error';
    let message = 'No internet connection detected';

    if (statusCode && statusCode >= 500) {
      title = 'Server Error';
      message =
        statusCode === 502 || statusCode === 503
          ? 'Server is temporarily unavailable. Please try again later.'
          : 'An internal server error occurred. Please try again later.';
    }

    if (!suppressGlobalErrorToast) {
      Store.dispatch(ShowToast({ title, message }));
    }
  }
});

const Client = (ssrMode = false): ApolloClient<NormalizedCacheObject> => {
  const { cache, link } = getClientParams(ssrMode);
  // const ssrLink = ApolloLink.from([errorLink, apolloLogger, link]);
  const ssrLink = ApolloLink.from([errorLink, link]);
  const clientLink = ApolloLink.from([error, invalidateOnMutationLink, link]);
  // ssrMode && console.log(`Client setup for ssrMode: ${ssrMode}`);
  return new ApolloClient({
    ssrMode,
    link: ssrMode ? ssrLink : clientLink,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    cache: cache as any,
  });
};

export { API_URL, ORIGIN_URL, ORIGIN_SERVER_URL };
export default Client;
